import React from 'react';
import { Entity } from '@backstage/catalog-model';
import { useApiHolder } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, InfoCardVariants, Link } from '@backstage/core-components';
import { useAsync } from 'react-use';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

/**
 * Props for {@link ContactChannelsCard}.
 *
 * @public
 */
export interface ContactChannelsCardProps {
  variant?: InfoCardVariants;
}

/**
 * An address to write to. The CMDB names it and says what it is for; the
 * `mailto:` is built by the provider, see ContactChannels in the catalog
 * backend module.
 */
type EmailLink = {
  name: string;
  address: string;
  url: string;
  purpose?: string[];
};

/**
 * A Jira project to file something in. `issueType`, `labels` and `components`
 * describe the issue that belongs in it rather than the project, so they are
 * shown next to it instead of being folded into the link.
 */
type JiraSpaceLink = {
  name: string;
  space: string;
  url: string;
  issueType?: string;
  labels?: string[];
  components?: string[];
  purpose?: string[];
};

/** What the entity carries as `spec.contactChannels`. */
type ContactChannels = {
  email?: EmailLink[];
  jiraSpaces?: JiraSpaceLink[];
};

const useStyles = makeStyles(theme => ({
  section: {
    '& + &': {
      marginTop: theme.spacing(3),
    },
  },
  heading: {
    marginBottom: theme.spacing(1),
  },
}));

/** A comma separated list, for the cells that hold one. */
const listOf = (values?: string[]) => (values ?? []).join(', ');

const Section = (props: {
  title: string;
  columns: string[];
  children: React.ReactNode;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <Typography variant="subtitle2" className={classes.heading}>
        {props.title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {props.columns.map(column => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{props.children}</TableBody>
      </Table>
    </div>
  );
};

/** Mail addresses are compared the way the catalog compares them. */
const mailKey = (address: string) => address.toLocaleLowerCase('en-US');

/** The name Entra gives the group, the one its members know it by. */
const groupNameOf = (group: Entity) =>
  (group.spec?.profile as { displayName?: string } | undefined)?.displayName ??
  group.metadata.title ??
  group.metadata.name;

/**
 * The name of the Entra group behind each address. Most addresses the CMDB
 * names are distribution lists, which the msgraph provider brings into the
 * catalog as groups (`VL...`) with the address as `spec.profile.email` and a
 * name a reader recognises. One request for every address on the card; where
 * the catalog is not at hand or has no such group, there is no name.
 */
function useMailGroupNames(
  addresses: string[],
): (address: string) => string | undefined {
  const catalogApi = useApiHolder().get(catalogApiRef);
  const keys = [...new Set(addresses.map(mailKey))].sort();
  // A fresh array on every render: the request is made again only when the
  // addresses change.
  const keysKey = keys.join(',');

  const { value: names } = useAsync(async () => {
    if (!catalogApi || keys.length === 0) {
      return new Map<string, string>();
    }
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Group', 'spec.profile.email': keys },
      fields: [
        'metadata.name',
        'metadata.title',
        'spec.profile.displayName',
        'spec.profile.email',
      ],
    });
    return new Map(
      items.flatMap(group => {
        const email = (group.spec?.profile as { email?: string } | undefined)
          ?.email;
        return email ? [[mailKey(email), groupNameOf(group)] as const] : [];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, keysKey]);

  return address => names?.get(mailKey(address));
}

/**
 * An address behind an Entra group is shown by the group's name - still a
 * `mailto:` of the address, which shows on hover - the bare address only where
 * there is no group.
 */
const EmailSection = (props: { links: EmailLink[] }) => {
  const nameOf = useMailGroupNames(props.links.map(link => link.address));
  return (
    <Section title="Email" columns={['Name', 'Recipient', 'Purpose']}>
      {props.links.map(link => (
        <TableRow key={`${link.name}-${link.address}`}>
          <TableCell>{link.name}</TableCell>
          <TableCell>
            <Link to={link.url} title={link.address}>
              {nameOf(link.address) ?? link.address}
            </Link>
          </TableCell>
          <TableCell>{listOf(link.purpose)}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
};

const JiraSpacesSection = (props: { links: JiraSpaceLink[] }) => (
  <Section
    title="Jira Spaces"
    columns={['Name', 'Space', 'Issue Type', 'Labels', 'Components', 'Purpose']}
  >
    {props.links.map(link => (
      <TableRow key={`${link.name}-${link.space}`}>
        <TableCell>
          <Link to={link.url}>{link.name}</Link>
        </TableCell>
        <TableCell>{link.space}</TableCell>
        <TableCell>{link.issueType}</TableCell>
        <TableCell>{listOf(link.labels)}</TableCell>
        <TableCell>{listOf(link.components)}</TableCell>
        <TableCell>{listOf(link.purpose)}</TableCell>
      </TableRow>
    ))}
  </Section>
);

/**
 * Where the CMDB says a team or a product is reached: addresses to write to,
 * Jira projects to file in.
 *
 * Neither belongs in the Links card. They are not a page to open, and each of
 * them carries fields of its own - what an address is for, what kind of issue
 * a project takes - which a title and a URL would throw away; a section per
 * kind is what lets them be shown. The web links and the Jira tickets of the
 * CMDB do go into that card, they are links like any other.
 *
 * A section with nothing in it is left out, and so is the whole card: most
 * teams and products carry none of this, and an empty card would only ask the
 * reader what is missing.
 *
 * @public
 */
export const ContactChannelsCard = (
  props: Readonly<ContactChannelsCardProps>,
) => {
  const { variant = 'gridItem' } = props;
  const { entity } = useEntity();
  const contactChannels = entity.spec?.contactChannels as
    | ContactChannels
    | undefined;

  const email = contactChannels?.email ?? [];
  const jiraSpaces = contactChannels?.jiraSpaces ?? [];

  if (email.length === 0 && jiraSpaces.length === 0) {
    return <></>;
  }

  return (
    <InfoCard title="Contact Channels" variant={variant}>
      {email.length > 0 && <EmailSection links={email} />}
      {jiraSpaces.length > 0 && <JiraSpacesSection links={jiraSpaces} />}
    </InfoCard>
  );
};
